/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, RadioGroupItem } from '@/components/forms/radio-group';
import { Button } from '@/components/buttons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/dialog';

export interface OriginalAddress {
    firstName: string;
    lastName: string;
    address1: string;
    address2?: string;
    city: string;
    stateCode?: string;
    postalCode?: string;
}

export interface SuggestedAddress {
    address1: string;
    address2?: string;
    city?: string;
    stateCode?: string;
    postalCode?: string;
    global_address_key?: string;
}

export type AddressValidationModalChoice = 'original' | 'suggested';

interface AddressValidationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Original address entered by user */
    originalAddress: OriginalAddress;
    /** Suggested address from Experian (when different) */
    suggestedAddress?: SuggestedAddress | null;
    /** No match - show error state */
    noMatch?: boolean;
    /** Multiple suggestions - user must pick one (first is shown as suggested when no suggestedAddress) */
    suggestions?: Array<{ text: string; global_address_key: string }>;
    onAccept: (choice: AddressValidationModalChoice, suggestedKey?: string) => void;
    onEdit: () => void;
}

function formatAddressLine(addr: OriginalAddress | SuggestedAddress, includeName = true): string {
    const parts: string[] = [];
    if (includeName && 'firstName' in addr && addr.firstName) parts.push(addr.firstName);
    if (includeName && 'lastName' in addr && addr.lastName) parts.push(addr.lastName);
    if (addr.address1) parts.push(addr.address1);
    if (addr.address2) parts.push(addr.address2);
    if (addr.city) parts.push(addr.city);
    if (addr.stateCode) parts.push(addr.stateCode);
    if (addr.postalCode) parts.push(addr.postalCode);
    return parts.filter(Boolean).join(', ');
}

export function AddressValidationModal({
    open,
    onOpenChange,
    originalAddress,
    suggestedAddress,
    noMatch = false,
    suggestions = [],
    onAccept,
    onEdit,
}: AddressValidationModalProps): ReactElement {
    const { t } = useTranslation('addressValidationModal');
    const [selected, setSelected] = useState<AddressValidationModalChoice>('suggested');

    // Reset to 'suggested' when modal opens so each open starts with correct default
    useEffect(() => {
        if (open) setSelected('suggested');
    }, [open]);

    const hasSuggested = !noMatch && (suggestedAddress || suggestions.length > 0);
    const showOriginalVsSuggested = hasSuggested && (suggestedAddress || suggestions.length > 0);

    const handleAccept = () => {
        if (noMatch) {
            onAccept('original');
        } else if (showOriginalVsSuggested) {
            if (selected === 'suggested') {
                const key = suggestedAddress?.global_address_key ?? suggestions[0]?.global_address_key;
                onAccept('suggested', key);
            } else {
                onAccept('original');
            }
        } else {
            onAccept('original');
        }
        // Parent owns modal lifecycle; do not call onOpenChange here to avoid double-close
    };

    const handleEdit = () => {
        onEdit();
        onOpenChange(false);
    };

    const originalFormatted = formatAddressLine(originalAddress);
    const suggestedFormatted = suggestedAddress
        ? formatAddressLine(
              {
                  ...originalAddress,
                  ...suggestedAddress,
              },
              true
          )
        : (suggestions[0]?.text ?? '');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold">
                        {noMatch ? t('verifyTitle') : t('validationTitle')}
                    </DialogTitle>
                    <DialogDescription asChild>
                        <p className="text-muted-foreground text-sm mt-2">
                            {noMatch ? t('noMatchMessage') : t('suggestedMessage')}
                        </p>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Original address */}
                    <div className="space-y-2">
                        {showOriginalVsSuggested ? (
                            <RadioGroup
                                value={selected}
                                onValueChange={(v) => setSelected(v as AddressValidationModalChoice)}
                                className="gap-3">
                                <div className="flex items-start gap-3">
                                    <RadioGroupItem value="original" id="original" className="mt-1" />
                                    <label htmlFor="original" className="flex-1 cursor-pointer">
                                        <span className="font-medium">{t('originalLabel')}</span>
                                        <p className="text-sm text-muted-foreground mt-1">{originalFormatted}</p>
                                    </label>
                                </div>
                                <div className="flex items-start gap-3">
                                    <RadioGroupItem value="suggested" id="suggested" className="mt-1" />
                                    <label htmlFor="suggested" className="flex-1 cursor-pointer">
                                        <span className="font-medium text-warning">{t('suggestedLabel')}</span>
                                        <p className="text-sm text-muted-foreground mt-1 text-warning/90">
                                            {suggestedFormatted}
                                        </p>
                                    </label>
                                </div>
                            </RadioGroup>
                        ) : (
                            <div>
                                <p className="font-medium">{t('originalLabel')}</p>
                                <p className="text-sm text-muted-foreground mt-1">{originalFormatted}</p>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:justify-end">
                    {(noMatch || showOriginalVsSuggested) && (
                        <Button variant="outline" onClick={handleEdit}>
                            {t('editAddress')}
                        </Button>
                    )}
                    <Button
                        variant="default"
                        className="bg-foreground text-background hover:bg-foreground/90"
                        onClick={handleAccept}>
                        {noMatch ? t('useAsEntered') : t('accept')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
